import React from 'react';
import dynamic from 'next/dynamic';
import { Box, Container, Divider, Grid, Link, Typography } from '@mui/material';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useStyles } from './styles';
import CustomButton from 'components/CustomButton';
import LazyLoad from 'react-lazyload';
import StepsSection from 'components/StepsSection';
import useIsMobile from 'hooks/useIsMobile';

const DynamicCircularCarousel = dynamic(() => import('components/CircularCarousel'));

const carouselMedicalPosters = [
  {
    image: '/images/medical-posters/template-slide-1.webp',
    alt: 'template slide 1 image',
    id: 1,
  },
  {
    image: '/images/medical-posters/template-slide-2.webp',
    alt: 'template slide 2 image',
    id: 2,
  },
  {
    image: '/images/medical-posters/template-slide-3.webp',
    alt: 'template slide 3 image',
    id: 3,
  },
  {
    image: '/images/medical-posters/template-slide-4.webp',
    alt: 'template slide 4 image',
    id: 4,
  },
  {
    image: '/images/medical-posters/template-slide-5.webp',
    alt: 'template slide 5 image',
    id: 5,
  },
  {
    image: '/images/medical-posters/template-slide-6.webp',
    alt: 'template slide 6 image',
    id: 6,
  },
];

export default function MainContent() {
  const router = useRouter();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const textButtons = 'Get Started';

  function handleURL() {
    return `/auth/login-register?from_url=${router.basePath + router.asPath}`;
  }

  return (
    <>
      <Box bgcolor="#F1F1F1" py={4}>
        <LazyLoad offset={100}>
          <Container>
            <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <NextLink href={handleURL()} passHref={true}>
                  <Link underline="hover">
                    <img
                      src="/images/medical-posters/professional-templates.webp"
                      alt="professional posters"
                      loading="lazy"
                      width={!isMobile ? '620' : '350'}
                      height={!isMobile ? '435' : '252'}
                    />
                  </Link>
                </NextLink>
              </Grid>

              <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                <img
                  src="/images/medical-posters/professional-templates-icon.svg"
                  alt="professional templates icon"
                  loading="lazy"
                  width="82.5"
                  height="76"
                />

                <Typography className={classes.primaryColor}>
                  <b>200+ professionally designed medical poster templates</b>
                </Typography>

                <Typography className={classes.textInfo}>
                  Create an effective & professional poster in minutes without any designer skills.
                </Typography>

                <NextLink href={handleURL()} passHref={true}>
                  <CustomButton>{textButtons}</CustomButton>
                </NextLink>
              </Grid>
            </Grid>

            <Box my={6}>
              <Divider />
            </Box>
          </Container>
        </LazyLoad>

        <LazyLoad offset={100}>
          <Container>
            <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
              <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                <img
                  src="/images/poster/poster-scientific-research-templates-icon.svg"
                  alt="scientific research template icon"
                  loading="lazy"
                  width="60"
                  height="64"
                />

                <Typography className={classes.primaryColor}>
                  <b>Highlight any finding with 40 000+ medical illustrations</b>
                </Typography>

                <Typography className={classes.textInfo}>
                  Poster illustrations for every topic. Browse through 1000s of professionally made
                  & scientifically accurate illustrations.
                </Typography>

                <NextLink href={handleURL()} passHref={true}>
                  <CustomButton>{textButtons}</CustomButton>
                </NextLink>
              </Grid>

              <Grid item={true} xs={12} md={6}>
                <NextLink href={handleURL()} passHref={true}>
                  <Link underline="hover">
                    <img
                      src="/images/medical-posters/medical-illustrations.webp"
                      alt="professional posters"
                      loading="lazy"
                      width={!isMobile ? '600' : '350'}
                      height={!isMobile ? '419' : '244'}
                    />
                  </Link>
                </NextLink>
              </Grid>
            </Grid>

            <Box my={6}>
              <Divider />
            </Box>
          </Container>
        </LazyLoad>

        <LazyLoad offset={100}>
          <Container>
            <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <NextLink href={handleURL()} passHref={true}>
                  <Link underline="hover">
                    <img
                      src="/images/medical-posters/make-impact.webp"
                      alt="professional posters"
                      loading="lazy"
                      width={!isMobile ? '600' : '350'}
                      height={!isMobile ? '432' : '252'}
                    />
                  </Link>
                </NextLink>
              </Grid>

              <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                <img
                  src="/images/medical-posters/megaphone-icon.svg"
                  alt="megaphone icon"
                  loading="lazy"
                  width="82.5"
                  height="76"
                />

                <Typography className={classes.primaryColor}>
                  <b>Make an impact with informative posters</b>
                </Typography>

                <Typography className={classes.textInfo}>
                  Create engaging posters that people will remember. Emphasize the most important
                  points of your research.
                </Typography>

                <NextLink href={handleURL()} passHref={true}>
                  <CustomButton>{textButtons}</CustomButton>
                </NextLink>
              </Grid>
            </Grid>
          </Container>
        </LazyLoad>
      </Box>

      <LazyLoad offset={100}>
        <Box display="flex" justifyContent="center" bgcolor="#5F29CC">
          <StepsSection
            title="Create Medical & biology poster in 4 simple steps"
            labels={[
              'Sign up to Mind The Graph and start the new project',
              'Pick a poster template or start from scratch',
              'Pick or customize project size you need to create',
              'Download your Medical & Biology poster in PNG, PDF, TIFF and other formats',
            ]}
          />
        </Box>
      </LazyLoad>

      <LazyLoad offset={100}>
        <Box bgcolor="#F1F1F1" py={6}>
          <Container>
            <Box display="flex" justifyContent="center" alignItems="center">
              <img
                src="/images/poster/poster-scientific-research-templates-icon.svg"
                alt="scientific research template icon"
                loading="lazy"
                width="60"
                height="64"
              />

              <Box ml={2}>
                <Typography className={classes.primaryColor} variant="h6" component="p">
                  <b>Explore more templates</b>
                </Typography>
              </Box>
            </Box>

            <Box mt={4}>
              <Typography align="center">
                With Mind The Graph's scientifically accurate medical templates visualize your{' '}
                <br /> research in cardiology, anatomy, human medicine and many others.
              </Typography>
            </Box>

            <Box px={!isMobile ? 12 : 0} pt={6} pb={2}>
              <DynamicCircularCarousel
                classes={{
                  root: classes.carouselRoot,
                  carousel: classes.carousel,
                  carouselItem: classes.carouselItem,
                }}
                autoplay={!isMobile ? false : true}
                controlButtons={!isMobile ? true : false}
                items={carouselMedicalPosters.map((carouselMedicalPosters) => (
                  <Box
                    key={carouselMedicalPosters.id}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <img
                      src={carouselMedicalPosters.image}
                      alt={carouselMedicalPosters.alt}
                      loading="lazy"
                      width={!isMobile ? '360' : '286'}
                      height={!isMobile ? '270' : '214'}
                    />
                  </Box>
                ))}
              />
            </Box>

            <Box display="flex" justifyContent="center" mt={4}>
              <NextLink href={handleURL()} passHref={true}>
                <CustomButton>{textButtons}</CustomButton>
              </NextLink>
            </Box>
          </Container>
        </Box>
      </LazyLoad>
    </>
  );
}
